
import React, { createContext, useContext, useState, useEffect } from "react";
import { phpApi } from "@/integrations/php/client";
import { useToast } from "@/components/ui/use-toast";

interface AssignmentContextType {
  getNextPartnerIdForAssignment: (serviceType: string) => Promise<string | null>;
  assignmentLogs: any[];
  refreshAssignmentLogs: () => Promise<void>;
}

const AssignmentContext = createContext<AssignmentContextType | undefined>(undefined);

export function useAssignment() {
  const context = useContext(AssignmentContext);
  if (context === undefined) {
    throw new Error("useAssignment must be used within an AssignmentProvider");
  }
  return context;
}

export const AssignmentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [assignmentLogs, setAssignmentLogs] = useState<any[]>([]);
  const { toast } = useToast();

  // Get assignment logs from the data layer
  const refreshAssignmentLogs = async (): Promise<void> => {
    try {
      console.info("Refreshing assignment stats...");
      const { data, error } = await phpApi
        .from('leads')
        .select(`
          id,
          partner_id,
          name,
          service,
          stage,
          created_at,
          updated_at,
          partners (
            id,
            name
          )
        `)
        .not('partner_id', 'is', null);

      if (error) {
        throw error;
      }

      const processedStats = processAssignmentData(data || []);
      console.info("Assignment stats refreshed:", processedStats);
      setAssignmentLogs(processedStats);
    } catch (error) {
      console.error("Error fetching assignment logs:", error);
      toast({
        variant: "destructive",
        title: "Σφάλμα",
        description: "Δεν ήταν δυνατή η φόρτωση των στοιχείων ανάθεσης.",
      });
    }
  };

  useEffect(() => {
    refreshAssignmentLogs();
  }, []);

  // Process the raw assignment data
  const processAssignmentData = (data: any[]) => {
    const partnerStats: Record<string, any> = {};

    const successStages = new Set(["έκλεισεΗΔουλειά", "ολοκληρώθηκε"]);
    const rejectedStages = new Set(["απορρίφθηκε"]);
    
    // Group by partner
    data.forEach(lead => {
      if (!lead.partner_id || !lead.partners) return;
      
      const partnerId = lead.partner_id;
      const partnerName = lead.partners.name;
      
      if (!partnerStats[partnerId]) {
        partnerStats[partnerId] = {
          partnerId,
          partnerName,
          totalAssignments: 0,
          successfulAssignments: 0,
          pendingAssignments: 0,
          rejectedAssignments: 0
        };
      }
      
      partnerStats[partnerId].totalAssignments++;
      
      // Count by stage
      if (successStages.has(lead.stage)) {
        partnerStats[partnerId].successfulAssignments++;
      } else if (rejectedStages.has(lead.stage)) {
        partnerStats[partnerId].rejectedAssignments++;
      } else {
        partnerStats[partnerId].pendingAssignments++;
      }
    });
    
    return Object.values(partnerStats);
  };

  // Round Robin assignment algorithm
  const getNextPartnerIdForAssignment = async (serviceType: string): Promise<string | null> => {
    try {
      // Fetch active partners that offer this service
      const { data: partners, error } = await phpApi
        .from('partners')
        .select('*')
        .eq('active', true)
        .contains('services', [serviceType])
        .order('created_at', { ascending: true });
      
      if (error || !partners || partners.length === 0) {
        console.error("Error fetching partners or no partners found:", error);
        return null;
      }
      
      // Fetch recent lead assignments to determine which partner should be next
      const { data: recentAssignments, error: recentError } = await phpApi
        .from('leads')
        .select('partner_id, created_at')
        .not('partner_id', 'is', null)
        .order('created_at', { ascending: false })
        .limit(partners.length * 2);
        
      if (recentError) {
        console.error("Error fetching recent assignments:", recentError);
        // If there's an error, just use the first partner
        return partners[0].id;
      }
      
      // If no recent assignments, assign to the first partner
      if (!recentAssignments || recentAssignments.length === 0) {
        return partners[0].id;
      }
      
      // Create a map of partner IDs for quick lookup
      const partnerIdMap = new Map(partners.map(p => [p.id, p]));
      
      // Find partners who haven't been assigned recently
      const recentlyAssignedIds = new Set(
        recentAssignments
          .filter(a => a.partner_id) // Filter out null partner_ids
          .map(a => a.partner_id)
      );
      
      // First, try to find partners who haven't been assigned at all
      const unassignedPartners = partners.filter(p => !recentlyAssignedIds.has(p.id));
      if (unassignedPartners.length > 0) {
        return unassignedPartners[0].id;
      }
      
      // If all partners have been assigned, find the one that was assigned least recently
      const assignmentOrder = recentAssignments
        .filter(a => partnerIdMap.has(a.partner_id)) // Only consider active partners
        .map(a => a.partner_id);
      
      // Find the first partner that appears latest in the recent assignments
      for (const partnerId of [...partnerIdMap.keys()]) {
        const lastIndex = assignmentOrder.lastIndexOf(partnerId);
        if (lastIndex === -1 || lastIndex === assignmentOrder.length - 1) {
          continue; // Skip if not found or most recently assigned
        }
        return partnerId;
      }
      
      // If we couldn't determine, pick the first partner
      return partners[0].id;
    } catch (error) {
      console.error("Error in round robin assignment:", error);
      return null;
    }
  };

  return (
    <AssignmentContext.Provider
      value={{
        getNextPartnerIdForAssignment,
        assignmentLogs,
        refreshAssignmentLogs
      }}
    >
      {children}
    </AssignmentContext.Provider>
  );
};
